import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { DeactivateOfferResultTypenames } from '@varo/graphql-schema-types';
import useDeactivateOffer from 'features/rewards/hooks/useDeactivateOffer';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { AbortError } from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import theme from 'shared/styles/theme';

export interface DeactivateBottomSheetProps {
  onConfirm?: () => void;
  onError?: (message?: string) => void;
  bottomSheetRef?: React.RefObject<BottomSheetModal | null>;
  offerId?: string;
  optimisticCloseOnPress?: boolean;
}

function DeactivateBottomSheet({
  onConfirm,
  onError,
  bottomSheetRef,
  offerId,
  optimisticCloseOnPress = false,
}: DeactivateBottomSheetProps) {
  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'deactivateBottomSheet',
  });
  const { deactivateOffer, fetching } = useDeactivateOffer();

  const handleDeactivateOfferFlow = async (): Promise<void> => {
    if (!offerId) {
      if (!optimisticCloseOnPress) {
        bottomSheetRef?.current?.dismiss();
        onError?.(translatedText('deactivatedSnackbarText'));
      }
      return;
    }
    try {
      const result = await deactivateOffer({ input: { offerId } });
      const typename = result.data?.deactivateOffer?.__typename;
      const isSuccess =
        typename === DeactivateOfferResultTypenames.DeactivateOfferSuccess;

      if (optimisticCloseOnPress) {
        return;
      }

      if (isSuccess) {
        onConfirm?.();
      } else {
        onError?.(translatedText('snackbarText'));
      }
      bottomSheetRef?.current?.dismiss();
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
    }
  };

  return (
    <BottomSheet
      ref={bottomSheetRef}
      contentContainerStyle={styles.bottomSheetContainer}
    >
      <View style={styles.contentContainer}>
        <TitleMdText numberOfLines={0}>{translatedText('title')}</TitleMdText>
        <BodyBaseText numberOfLines={0}>
          {translatedText('bodyMessage')}
        </BodyBaseText>
      </View>

      <View style={styles.buttonContainer}>
        <Button
          label={translatedText('buttonTitle')}
          type="PRIMARY"
          onPress={() => {
            if (optimisticCloseOnPress) {
              onConfirm?.();
              bottomSheetRef?.current?.dismiss();
            }
            void handleDeactivateOfferFlow();
          }}
          analyticsId="deactivateOffer"
          isLoading={fetching}
        />

        <Button
          label={translatedText('cancelButtonTitle')}
          type="SECONDARY"
          onPress={() => {
            bottomSheetRef?.current?.dismiss();
          }}
          analyticsId="cancelDeactivateOffer"
        />
      </View>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  bottomSheetContainer: {
    paddingTop: 0,
  },
  buttonContainer: {
    padding: theme.spacing.m,
    gap: theme.spacing.m,
  },
  contentContainer: {
    flex: 1,
    gap: theme.spacing.s,
    padding: theme.spacing.m,
  },
});

export default DeactivateBottomSheet;
